import type { OrgUnitTreeNode } from './organizationTypes';

/** Width of a root unit's number, so 1 reads as 0001 and sorts as text the way it counts. */
const ROOT_WIDTH = 4;

/**
 * The code to offer for a new unit under a given parent.
 *
 * The scheme is a **path**: a unit's code is its parent's code, a hyphen, and the next free
 * number among that parent's children — `0001` gets `0001-1`, then `0001-2`. A root unit
 * gets the next free number at the top level, zero-padded. So the code says where the unit
 * sits, and a reader looking at `0001-2-3` knows its depth without opening anything.
 *
 * Three things this deliberately is **not**:
 *
 * - **Not a guarantee of uniqueness.** Codes carry no unique index, and this reads only the
 *   chart that is currently loaded — with deleted units hidden, a suggestion can collide
 *   with a deleted unit's code. A collision is untidy rather than an error, and the field
 *   stays editable, so the reader can settle it.
 * - **Not derived from the name.** The name is free prose and changes; the position is what
 *   the code encodes.
 * - **Not applied to an existing unit.** A stored code is data the reader chose, and a
 *   suggestion must never overwrite it.
 */
export function suggestChildCode(
  nodes: readonly OrgUnitTreeNode[],
  parentId: string | null,
): string {
  if (parentId === null) {
    return nextRootCode(nodes);
  }

  const parent = findNode(nodes, parentId);

  if (parent === null) {
    return '';
  }

  const prefix = parent.code?.trim();

  // Nothing to build a path on. Better an empty field the reader fills in than a code
  // invented from somewhere else in the chart.
  if (!prefix) {
    return '';
  }

  return `${prefix}-${nextSuffix(parent.children, `${prefix}-`)}`;
}

/**
 * The next free number at the top level, padded.
 *
 * Padded because the existing roots are: offering `2` beside `0001` would put two spellings
 * of the same scheme in one column.
 */
function nextRootCode(nodes: readonly OrgUnitTreeNode[]): string {
  let highest = 0;

  for (const node of nodes) {
    const code = node.code?.trim();

    // Only a bare number counts as a root code in this scheme. Anything else is a code
    // somebody typed by hand, and it is not this function's place to renumber around it.
    if (code && /^\d+$/.test(code)) {
      highest = Math.max(highest, Number(code));
    }
  }

  return String(highest + 1).padStart(ROOT_WIDTH, '0');
}

/**
 * The next free suffix under a prefix.
 *
 * Highest-plus-one rather than count-plus-one: children get deleted and the tree renumbers
 * around nothing, so counting them would re-offer a code a sibling already holds.
 */
function nextSuffix(children: readonly OrgUnitTreeNode[], prefix: string): number {
  let highest = 0;

  for (const child of children) {
    const code = child.code?.trim();

    if (!code?.startsWith(prefix)) {
      continue;
    }

    const suffix = code.slice(prefix.length);

    if (/^\d+$/.test(suffix)) {
      highest = Math.max(highest, Number(suffix));
    }
  }

  return highest + 1;
}

/** Depth-first, because the parent may be anywhere in the chart. */
function findNode(
  nodes: readonly OrgUnitTreeNode[],
  id: string,
): OrgUnitTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }

    const found = findNode(node.children, id);

    if (found !== null) {
      return found;
    }
  }

  return null;
}
